import { useState, useEffect } from "react";
import { Plus, MoreHorizontal, Shield, Users, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { BadgeVenture } from "@/components/ui/badge-venture";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { toast } from "sonner";

interface AdminUser {
  id: string;
  full_name: string;
  email: string;
  role: string;
  status: string;
}

function getRoleLabel(role: string) {
  switch (role) {
    case "super_admin": return "Super Admin";
    case "team_admin": return "Team Admin";
    case "member": return "Member";
    default: return role;
  }
}

function getInitials(name: string) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function TeamSettings() {
  const { role } = useRole();
  const [teamMembers, setTeamMembers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (role !== "admin") {
      setLoading(false);
      return;
    }

    const fetchTeam = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, full_name, email, role, status')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) {
        toast.error("Failed to load team members");
        console.error(error);
      } else {
        setTeamMembers((data || []) as AdminUser[]);
      }
      setLoading(false);
    };

    fetchTeam();
  }, [role]);

  const handleAddMember = () => {
    toast.info("Contact your account manager to add team members");
  };

  // Client view
  if (role === "client") {
    return (
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Team Members</h3>
            <p className="text-sm text-muted-foreground">Manage who has access to your account</p>
          </div>
          <Button variant="gradient" onClick={handleAddMember}>
            <Plus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>

        <VentureCard>
          <VentureCardContent className="py-8">
            <p className="text-sm text-muted-foreground text-center">
              Team management is handled by your Venture Hills account manager.
              Contact support to add or remove team members.
            </p>
          </VentureCardContent>
        </VentureCard>
      </div>
    );
  }

  // Admin view
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Team Members</h3>
          <p className="text-sm text-muted-foreground">Manage who has access to your account</p>
        </div>
        <Button variant="gradient" onClick={handleAddMember}>
          <Plus className="w-4 h-4 mr-2" />
          Add Team Member
        </Button>
      </div>

      <VentureCard>
        <VentureCardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : teamMembers.length === 0 ? (
            <div className="py-8">
              <p className="text-sm text-muted-foreground text-center">No team members found</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <span className="text-sm font-bold text-primary-foreground">{getInitials(member.full_name)}</span>
                    </div>
                    <div>
                      <p className="font-medium">{member.full_name}</p>
                      <p className="text-sm text-muted-foreground">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <BadgeVenture variant={member.role === "super_admin" ? "info" : "default"}>
                      {member.role === "super_admin" || member.role === "team_admin" ? (
                        <Shield className="w-3 h-3 mr-1" />
                      ) : (
                        <Users className="w-3 h-3 mr-1" />
                      )}
                      {getRoleLabel(member.role)}
                    </BadgeVenture>
                    <button className="p-2 rounded-lg hover:bg-accent transition-colors">
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </VentureCardContent>
      </VentureCard>

      <VentureCard>
        <VentureCardHeader>
          <VentureCardTitle>Pending Invitations</VentureCardTitle>
        </VentureCardHeader>
        <VentureCardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No pending invitations
          </p>
        </VentureCardContent>
      </VentureCard>
    </div>
  );
}
